import React, { FC, useState, useCallback } from "react";
import { Box, CircularProgress, Chip, Menu, MenuItem } from "@mui/material";
import { observer } from "mobx-react";

import { LineItemCorrectionBillInfoProps } from "../types/LineItemCorrectionBill.types";
import { DeleteConfirmationDialog } from "../../../DirectBilling/common/exports";
import { WaitingBackdrop } from "../../../../Base/components/BackDrop";
import useLineItemCorrectionBillInfo from "../hooks/useLineItemCorrectionBillInfo";
import { CorrectionHeaderActions } from "../../../CorrectionBilling/New/components/sub-components/CorrectionHeaderActions";
import CorrectionBillCardNew from "../../../CorrectionBilling/New/components/sub-components/CorrectionBillCardNew";
import TransactionSpotlightSearch from "../../../CorrectionBilling/New/components/sub-components/TransactionSpotlightSearch";

const LineItemCorrectionBillInfo: FC<LineItemCorrectionBillInfoProps> =
  observer((props) => {
    const {
      // state
      openDeleteDialog,
      tableVisible,
      isLoading,
      correctionBills,
      notes,
      isSaving,

      // handlers
      setSelectedRxId,
      handleSave,
      handleDeleteCancel,
      handleDeleteConfirm,
      handleNotesChange,
      handleDeleteClick,
      handleRemoveAll,
      handleRxsFromTransactionSelected,

      // from props / from hook
      onRowClick,
      selectedRxId,
      anchorEl,
      handleMenuOpen,
      handleMenuClose,
      account,
      userName,
    } = useLineItemCorrectionBillInfo(props);

    const [isSpotlightOpen, setIsSpotlightOpen] = useState(false);

    // State for stepper
    const [activeStep, setActiveStep] = useState(0);

    const computeStep = useCallback(() => {
      if (correctionBills.length > 0) {
        if (isSaving) {
          return 1;
        } else {
          return 0;
        }
      }
      return 0;
    }, [correctionBills.length, isSaving]);

    React.useEffect(() => {
      setActiveStep(computeStep());
    }, [computeStep]);

    const handleSaveClicked = async () => {
      await handleSave();
    };

    return (
      <Box
        sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
      >
        <WaitingBackdrop isWorking={isSaving} message="Bitte warten" />
        {/* Header Buttons */}
        <Box
          sx={{
            padding: 1,
            position: "sticky",
            top: 0,
            zIndex: 1000,
            backgroundColor: "#5c758a",
            borderRadius: "0px 0px 10px 10px",
          }}
        >
          <CorrectionHeaderActions
            activeStep={activeStep}
            tableVisible={tableVisible}
            onAddRx={() => setIsSpotlightOpen(true)}
            onFilterValid={() => {}}
            onSaveBills={handleSaveClicked}
          />
        </Box>

        {/* Search Transaction Number Modal */}
        <TransactionSpotlightSearch
          accountId={account}
          onRxsSelected={(rxPreviews) => {
            handleRxsFromTransactionSelected(rxPreviews);
            setIsSpotlightOpen(false);
          }}
          isLoading={isLoading}
          setIsLoading={() => {}}
          isOpen={isSpotlightOpen}
          onClose={() => setIsSpotlightOpen(false)}
        />

        {/* Loading */}
        {isLoading && !isSpotlightOpen && (
          <Box className="loading-container">
            <CircularProgress color="primary" />
            <Chip
              label="Daten werden geladen, bitte warte..."
              className="loading-chip"
            />
          </Box>
        )}

        {tableVisible &&
          correctionBills.length > 0 &&
          correctionBills.map((correctionBill) => (
            <Box className="bill-card" key={correctionBill.payer.ik}>
              <CorrectionBillCardNew
                bill={correctionBill}
                account={account}
                userName={userName}
                notes={notes[correctionBill.payer.ik] || ""}
                onNotesChange={(val: string) =>
                  handleNotesChange(correctionBill.payer.ik, val)
                }
                selectedRxId={selectedRxId}
                onRowClick={(row: any) => {
                  onRowClick(row);
                  setSelectedRxId(row.id);
                }}
                onRemoveAll={handleRemoveAll}
                onMenuOpen={handleMenuOpen}
              />
            </Box>
          ))}

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          open={openDeleteDialog}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleDeleteClick}>Entfernen</MenuItem>
        </Menu>
      </Box>
    );
  });

export default LineItemCorrectionBillInfo;
